import { getCountryCallingCode, isSupportedCountry, type CountryCode } from "libphonenumber-js/max";

export interface EnquiryPhoneCountry {
  code: CountryCode;
  name: string;
  dialCode: string;
}

// Online country directory maintained by https://github.com/mledoze/countries (ODbL).
const directoryUrl = "https://raw.githubusercontent.com/mledoze/countries/master/countries.json";

export function parseEnquiryPhoneCountries(data: unknown): EnquiryPhoneCountry[] {
  if (!Array.isArray(data)) throw new Error("The country directory returned an invalid response.");
  const countries = new Map<string, EnquiryPhoneCountry>();
  for (const entry of data) {
    if (!entry || typeof entry !== "object") continue;
    const { cca2, name, idd } = entry;
    if (typeof cca2 !== "string" || !isSupportedCountry(cca2) || typeof name?.common !== "string" || !name.common.trim()) continue;
    if (typeof idd?.root !== "string" || !Array.isArray(idd.suffixes)) continue;
    const code = cca2 as CountryCode;
    const callingCode = `+${getCountryCallingCode(code)}`;
    // NANP entries include area codes in their suffixes; these belong in the national number.
    const prefix = idd.suffixes.map((suffix: unknown) => typeof suffix === "string" ? `${idd.root}${suffix}` : "")
      .find((value: string) => /^\+\d+$/.test(value) && value.startsWith(callingCode));
    if (prefix) countries.set(code, { code, name: name.common.trim(), dialCode: prefix.slice(0, callingCode.length) });
  }
  if (!countries.size) throw new Error("No phone countries were available in the directory.");
  return [...countries.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export async function fetchEnquiryPhoneCountries(signal?: AbortSignal): Promise<EnquiryPhoneCountry[]> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  const timeout = setTimeout(abort, 15_000);
  try {
    const response = await fetch(directoryUrl, { signal: controller.signal, credentials: "omit", referrerPolicy: "no-referrer" });
    if (!response.ok) throw new Error("The country directory is unavailable.");
    return parseEnquiryPhoneCountries(await response.json());
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}
